import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {useFormik} from "formik";
import CustomerActions from "../../api/CustomerActions";
import {useNotification} from "../../hooks/useNotification";
import PageLayout from "../../components/PageLayout/PageLayout";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import classNames from "classnames";
import Icon, {arrowIcon, customerIcon} from "../../components/Icon/Icon";
import FormReader from "../../components/FormReader/FormReader";
import baseFormStyles from "../../components/BaseForm.module.scss";
import {getRootItems} from "../../utils/Utils";
import * as yup from "yup";


const validationSchema = yup.object({
    name: yup.string().required("Customer first name is required"),
    surname: yup.string().required("Customer last name is required"),
    email: yup.string().email("Email data is incorrect").required("Email is required"),
    customerForms: yup.array().of(
        yup.lazy((value) => {
            const droppedItems = value?.form?.content?.droppedItems || [];
            const requiredInputs = droppedItems.filter(item => item.type === "input" && item.settings?.required);

            const dataShape = {};
            requiredInputs.forEach(item => {
                if (item.settings.inputType === 'radio') {
                    dataShape[item.settings.selectedRadioGroup.value] = createFieldSchema(item);
                } else {
                    dataShape[item.id] = createFieldSchema(item);
                }
            });

            return yup.object({
                data: yup.object().shape(dataShape)
            });
        })
    )
});

const createFieldSchema = (droppedItem) => {
    const required = droppedItem.settings.required;
    const requiredMessage = `${droppedItem.settings.title || 'This field'} is required`;

    if(droppedItem.settings.title === "Phone number"){
        return required ?
            yup.string().required(requiredMessage).matches(/^[0-9]{9}$/, `Phone number can only contain 9 numbers`)
            : yup.string().matches(/^[0-9]{9}$/, `Phone number can only contain 9 numbers`);
    }
    else if(droppedItem.settings.title === "Postal code"){
        return required ?
            yup.string().required(requiredMessage).matches(/^\\d{2}[-]\\d{3}$/, `Postal code can only contain  numbers and "-"`)
            : yup.string().matches(/^\\d{2}[-]\\d{3}$/, `Postal code can only contain  numbers and "-"`);
    }
    else if (droppedItem.settings.inputType === "number"){
        return required ?
            yup.number().required(requiredMessage).positive("Number type should be positive").integer("Number type should be integer")
            : yup.number().positive("Number type should be positive").integer("Number type should be integer");
    }
    else if (droppedItem.settings?.inputType === 'signature') {
        return yup.mixed().nullable();
    }
    return required ? yup.string().required(requiredMessage) : yup.string();
};


const EditCustomer = () => {
    const { id } = useParams();
    const [visibleForms, setVisibleForms] = useState([]);
    const navigate = useNavigate();
    const {triggerNotification} = useNotification();

    const formik = useFormik({
        initialValues: {
            name: null,
            surname: null,
            email: null,
            phoneNumber: null,
            customerForms: []
        },
        validationSchema,
        onSubmit: values => {
            if (values.id) {
                CustomerActions.updateCustomer(id, values, () => {
                    triggerNotification("Customer updated successfully")
                    navigate("/customers");
                });
            } else {
                CustomerActions.createCustomer(values, () => {
                    triggerNotification("Customer created successfully")
                    navigate("/customers");
                });
            }
        }
    })

    useEffect(() => {
        if(!id) return;

        CustomerActions.getCustomer(id, res => formik.setValues(res));
    }, [id]);

    const toggleVisibleForms = (index) => {
        setVisibleForms(prev => {
            if (prev.includes(index)) {
                return prev.filter(form => form !== index);
            } else {
                return [...prev, index];
            }
        })
    }

    const handleFormChange = (formIndex, inputId, value) => {
        formik.setFieldValue(`customerForms[${formIndex}].data.${inputId}`, value);
    }


    return (
        <PageLayout
            titlePage="Customers"
            icon={customerIcon}
            titlePageContent={formik.values.id ? "Edit customer" : "New Customer"}
            actions={
                <>
                    <Button type="submit" form="customer-form">Save</Button>
                    <Button to="/customers" variant="secondary" className="cancel">Cancel</Button>
                </>
            }
            className={baseFormStyles["base-form"]}
        >
            <form  id="customer-form" onSubmit={formik.handleSubmit}>
                <Input
                    name="name"
                    label="First Name"
                    formik={formik}
                />
                <Input
                    name="surname"
                    label="Last Name"
                    formik={formik}
                />
                <Input
                    name="email"
                    label="Email address"
                    formik={formik}
                />
                <Input
                    name="phoneNumber"
                    label="Phone Number"
                    required={false}
                    formik={formik}
                />
            </form>
            {
                formik.values.customerForms.map((form, index) => {
                        const droppedItems = form.form.content?.droppedItems ?? [];
                        const rootItems = getRootItems(droppedItems)

                        const isVisible = visibleForms.includes(index);

                        return (
                            <>
                                <Button
                                    variant="show-form"
                                    className={classNames({"active": isVisible})}
                                    onClick={() => toggleVisibleForms(index)}
                                >
                                    {form.form.name}
                                    <Icon svg={arrowIcon}/>
                                </Button>

                                <FormReader
                                    rootItems={rootItems}
                                    droppedItems={droppedItems}
                                    className={classNames({"active": isVisible})}
                                    data={form.data}
                                    onChange={(inputId, value) => handleFormChange(index, inputId, value)}
                                    formId={form.form.id}
                                    customerId={formik.values.id}
                                    errors={formik.errors.customerForms?.[index]?.data}
                                    url={form.url}
                                />
                            </>
                        )
                    }
                )
            }
        </PageLayout>
    );
}

export default EditCustomer;